import { FragmentGapFillMethod } from "@aec-craft/nb-generative-core";
import { inputConstraints } from "@app/actions";
import { z } from "@hono/zod-openapi";
import type { PipeSettings } from "@prisma/client-generated";

export type UnitMix = Record<
	string,
	{
		value: number;
		locked: boolean;
	}
>;

export const zLoadType = z.enum(["double", "single"]);
export type LoadType = z.infer<typeof zLoadType>;

export const zTemplateLoadType = z.enum(["double", "single", "corner"]);
export type TemplateLoadType = z.infer<typeof zTemplateLoadType>;
export const zFragmentGapFillMethod = z.enum(FragmentGapFillMethod);
export const zStoredFragmentGapFillMethod = zFragmentGapFillMethod.catch(
	FragmentGapFillMethod.expandLargestUnit,
);

const zParkingSpaces = z
	.number()
	.min(inputConstraints.parkingSpaces.min)
	.max(inputConstraints.parkingSpaces.max);
const zParkingApartmentArea = z
	.number()
	.min(inputConstraints.parkingApartmentArea.min)
	.max(inputConstraints.parkingApartmentArea.max);

export const zParkingFactors = z.object({
	apartmentTypes: z
		.array(
			z.object({
				id: z.string(),
				apartmentGroupId: z.string(),
				spaces: zParkingSpaces,
			}),
		)
		.min(1)
		.optional(),
	apartmentAreaRanges: z
		.array(
			z
				.object({
					id: z.string(),
					fromArea: zParkingApartmentArea,
					toArea: zParkingApartmentArea,
					spaces: zParkingSpaces,
				})
				.refine(({ fromArea, toArea }) => toArea > fromArea, {
					message: "Parking apartment area range must end above its start",
				}),
		)
		.min(1)
		.optional(),
	totalArea: z
		.object({
			areaBasis: z
				.number()
				.min(inputConstraints.parkingAreaBasis.min)
				.max(inputConstraints.parkingAreaBasis.max),
			spaces: zParkingSpaces,
		})
		.optional(),
});
export type ParkingFactors = z.infer<typeof zParkingFactors>;

export const zPipeSettings = z.object({
	id: z.string(),
	massingSeed: z.number().int().min(0),
	unitMixGroupId: z.string(),
	layoutId: z.string(),
	minWingLength: z.number(),
	maxWingLength: z.number(),
	wingMinSetback: z.number(),
	wingSetbackFactor: z.number(),
	corridorWidth: z
		.number()
		.min(inputConstraints.corridorWidth.min)
		.max(inputConstraints.corridorWidth.max),
	unitHeight: z.number(),
	unitDepth: z
		.number()
		.min(inputConstraints.unitDepth.min)
		.max(inputConstraints.unitDepth.max),
	maxLevels: z.number().int().min(1).max(30),
	commercialHeight: z.number().min(0),
	maxGsi: z.number().min(0).max(1),
	maxAncillaryGsi: z.number().min(0).max(1),
	targetFar: z.number().min(0),
	parkingMultiplier: z
		.number()
		.min(inputConstraints.parkingMultiplier.min)
		.max(inputConstraints.parkingMultiplier.max),
	parkingFactors: zParkingFactors,
	facadeWallThickness: z.number().min(0),
	partitionWallThickness: z.number().min(0),
	structuralFloorThickness: z.number().min(0),
	finishingFloorThickness: z.number().min(0),
	roofThickness: z.number().min(0),
	maxFragmentLength: z
		.number()
		.min(inputConstraints.maxFragmentLength.min)
		.max(inputConstraints.maxFragmentLength.max),
	maxCornerFragmentLength: z
		.number()
		.min(inputConstraints.maxCornerFragmentLength.min)
		.max(inputConstraints.maxCornerFragmentLength.max),
	maxCornerFragmentLengthOn: z.boolean(),
	maxGsiOn: z.boolean(),
	maxAncillaryGsiOn: z.boolean(),
	targetFarOn: z.boolean(),
	balconiesAffectMassing: z.boolean(),
	corridorEndApartments: z.boolean(),
	shiftedCorners: z.boolean(),
	fragmentGapFillMethod: zFragmentGapFillMethod,
	resizeCornerCores: z.boolean(),
	avoidNorthFactor: z
		.number()
		.min(inputConstraints.avoidNorthFactor.min)
		.max(inputConstraints.avoidNorthFactor.max),
	unitMixOn: z.boolean(),
	loadType: zLoadType,
	apartmentSetId: z.string(),
}) satisfies z.ZodType<PipeSettings>;

const zPipelineState = zPipeSettings.omit({
	id: true,
	layoutId: true,
	unitMixGroupId: true,
	apartmentSetId: true,
});
export type PipelineState = z.infer<typeof zPipelineState>;

export const zPipelineStateReq = zPipelineState.partial({
	fragmentGapFillMethod: true,
	resizeCornerCores: true,
});
export type PipelineStateReq = z.infer<typeof zPipelineStateReq>;
